// The insight dashboard — what clicking a measurement in the Dynamic Pulse rail
// opens. A MODAL, not a hard page: the ONE drill surface for any series in the
// battery (surfaced, quiet, or the anchor), so the instrument never needs
// per-area sub-pages. Built on `PeekDialog` — THE large content-dialog standard
// (Matt, 2026-07-18): sanctioned geometry, a `ModalHeader` title block, and the
// Expand+Close ghost pair adjacent top-right. What that adoption fixes here:
//
//   · the narrative HEADLINE sentence is the PeekDialog title ("X ran N× its
//     learned baseline this week — <slice> carried M% of the move"), the section
//     label its quiet eyebrow, the baseline→this-week subhead its description.
//     NO tag sits with the title: the finding tone (needs-attention / improved /
//     in-range / anchor) moves DOWN into the stat band — the "This week"
//     `StatCard`'s `leadingDot` + label — where a state cue belongs;
//   · Expand routes to the series' filtered view (`viewHref`) and Close dismisses,
//     the two ghost icon-buttons together top-right. The rail hosting this dialog
//     outlives the route, so Expand also closes (`onExpand`) — no modal stranded
//     over the destination. This retires the old footer "Open view" action;
//   · the honest TrendChart — the LEARNED baseline band (trimmed mean ± robust
//     spread, floored at 0 for non-negative series) painted behind the daily
//     line, the recent window shaded, gaps left as gaps (no fabricated
//     interpolation), a hover crosshair. Geometry unchanged; theme vars only;
//   · a compact stat band of four `StatCard`s (this week + tone · learned baseline
//     ± spread · deviation z + volume weight · anchor coupling);
//   · the slice decomposition as a flush `DataTable` (sparkline · baseline→this-week
//     · share-of-move · deviation) under a `PeekDialogSection` heading;
//   · the honest-math note. The `actions` slot carries the remaining P4 footer
//     actions (watch · propose a fix → Play) — same modal.
//
// ONE surface: everything sits directly on the dialog's own `bg-popover` — no
// nested translucent panels; PeekDialog owns the `sm:max-w-3xl max-h-[85vh]`
// geometry and pins the header while the body scrolls.
//
// The types are the wire contract's (`api/contracts/pulse-telemetry`), so
// `packages/ui` stays free of any `excellent/src` import.

import * as React from "react";
import { PeekDialog, PeekDialogSection } from "../peek-dialog";
import { Sparkline } from "../sparkline";
import { StatCard } from "../stat-card";
import { DataTable, type DataTableColumn } from "../data-table";
import { cn } from "../../utils";
import { formatDate, formatUsd } from "../../format";
import type {
  SectionTelemetryDTO,
  TelemetrySeriesDTO,
} from "../../api/contracts/pulse-telemetry";

/** A learned series' measurement unit (derived from the wire contract). */
export type TelemetryUnit = TelemetrySeriesDTO["unit"];

// ── formatting (money through the shared formatter; the rest unit-local) ──────
// The compact per-unit read-out shared by the rail rows, the peek, and this
// modal. Money always routes through `formatUsd` (the display-consistency rail);
// the compact per-day / pct / duration forms are chart-internal axis labels the
// shared formatters don't cover.
// `null` = the window carries NO reading (a ratio/level series whose recent
// denominator is 0). It renders as an em-dash rather than a number, because the
// alternative — the engine's old `median(baselineVals)` fallback — drew an
// OLDER window's value as though it were the current one.
export const NO_READING = "—";

export function fmtTelemetry(unit: TelemetryUnit, v: number | null): string {
  if (v === null) return NO_READING;
  switch (unit) {
    case "per-day": {
      const r = Math.round(v * 10) / 10;
      return `${Number.isInteger(r) ? r : r.toFixed(1)}/day`;
    }
    case "pct":
      return `${Math.round(v)}%`;
    case "usd":
      return formatUsd(v);
    case "hours":
      return Math.abs(v) >= 48 ? `${(v / 24).toFixed(1)}d` : `${v.toFixed(1)}h`;
    case "minutes":
      return `${v.toFixed(1)}m`;
  }
}

// ── the trend chart (single series · baseline band · hover readout) ───────────
// Responsive by construction: a fixed `viewBox` with `className="h-auto w-full"`
// so the SVG scales to the dialog column — never a fixed pixel width that could
// overrun the panel. Geometry unchanged from the shipped chart; colors are all
// theme vars.
const W = 640;
const H = 210;
const L = 10;
const R = 56;
const T = 12;
const B = 24;

function TrendChart({
  series,
  days,
  recentDays,
}: {
  series: TelemetrySeriesDTO;
  days: string[];
  recentDays: number;
}) {
  const [hoverI, setHoverI] = React.useState<number | null>(null);
  const pts = series.points;
  const nums = pts.filter((v): v is number => v != null);
  if (nums.length < 2) {
    return <p className="py-8 text-center type-body text-muted-foreground">Not enough history to chart.</p>;
  }

  // Band floor: a series whose observations never go negative (counts, spend)
  // must not paint a "normal" band below zero.
  const floor = nums.every((v) => v >= 0) ? 0 : -Infinity;
  const b0 = Math.max(floor, series.baseline - series.spread);
  const b1 = series.baseline + series.spread;
  let yMin = Math.min(...nums, b0);
  let yMax = Math.max(...nums, b1);
  const pad = (yMax - yMin || 1) * 0.08;
  yMin -= pad;
  yMax += pad;

  const n = pts.length;
  const x = (i: number) => L + (i * (W - L - R)) / (n - 1);
  const y = (v: number) => T + (1 - (v - yMin) / (yMax - yMin)) * (H - T - B);

  // contiguous non-null runs → path segments; isolated points → dots
  const segments: string[] = [];
  const lonePoints: number[] = [];
  let run: number[] = [];
  const flush = () => {
    if (run.length >= 2) {
      segments.push(run.map((i, k) => `${k === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(pts[i] as number).toFixed(1)}`).join(""));
    } else if (run.length === 1) {
      lonePoints.push(run[0]!);
    }
    run = [];
  };
  for (let i = 0; i < n; i++) {
    if (pts[i] != null) run.push(i);
    else flush();
  }
  flush();

  const recentX = x(Math.max(0, n - recentDays));
  const xTicks = [0, Math.floor(n / 3), Math.floor((2 * n) / 3), n - 1];
  const yTicks: number[] = [yMax - pad, series.baseline, yMin + pad];

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const fx = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((fx - L) / (W - L - R)) * (n - 1));
    setHoverI(Math.max(0, Math.min(n - 1, i)));
  };

  const hv = hoverI != null ? pts[hoverI] : null;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        onPointerMove={onMove}
        onPointerLeave={() => setHoverI(null)}
        role="img"
        aria-label={`${series.label}: daily values with the learned baseline band`}
      >
        {/* recent-window shading */}
        <rect x={recentX} y={T} width={W - R - recentX} height={H - T - B} style={{ fill: "var(--primary)" }} opacity={0.05} />
        {/* learned baseline band + midline */}
        <rect x={L} y={y(b1)} width={W - L - R} height={Math.max(1, y(b0) - y(b1))} style={{ fill: "var(--muted)" }} opacity={0.5} />
        <line x1={L} x2={W - R} y1={y(series.baseline)} y2={y(series.baseline)} style={{ stroke: "var(--muted-foreground)" }} strokeOpacity={0.5} strokeDasharray="4 4" strokeWidth={1} />
        {/* recessive frame lines */}
        {yTicks.map((v) => (
          <line key={v} x1={L} x2={W - R} y1={y(v)} y2={y(v)} style={{ stroke: "var(--border)" }} strokeOpacity={0.35} strokeWidth={1} />
        ))}
        {/* the series */}
        {segments.map((d, i) => (
          <path key={i} d={d} fill="none" style={{ stroke: "var(--chart-4)" }} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" strokeOpacity={0.85} />
        ))}
        {lonePoints.map((i) => (
          <circle key={i} cx={x(i)} cy={y(pts[i] as number)} r={2.5} style={{ fill: "var(--chart-4)" }} opacity={0.85} />
        ))}
        {/* hover crosshair + dot */}
        {hoverI != null && (
          <>
            <line x1={x(hoverI)} x2={x(hoverI)} y1={T} y2={H - B} style={{ stroke: "var(--border)" }} strokeWidth={1} />
            {hv != null && <circle cx={x(hoverI)} cy={y(hv)} r={3.5} style={{ fill: "var(--chart-4)" }} />}
          </>
        )}
        {/* y labels (right) */}
        {yTicks.map((v) => (
          <text key={`yl${v}`} x={W - R + 8} y={y(v) + 3} className="type-subtext fill-muted-foreground">
            {fmtTelemetry(series.unit, v)}
          </text>
        ))}
        {/* x labels */}
        {xTicks.map((i, k) => (
          <text
            key={`xl${i}`}
            x={x(i)}
            y={H - 8}
            textAnchor={k === 0 ? "start" : k === xTicks.length - 1 ? "end" : "middle"}
            className="type-subtext fill-muted-foreground"
          >
            {formatDate(days[i]!)}
          </text>
        ))}
      </svg>
      {hoverI != null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-md border border-border bg-popover px-2 py-1 type-subtext shadow-md"
          style={{
            left: `${(x(hoverI) / W) * 100}%`,
            top: hv != null ? `calc(${(y(hv) / H) * 100}% - 34px)` : "8px",
          }}
        >
          <span className="text-muted-foreground">{formatDate(days[hoverI]!)}</span>{" "}
          <span className="font-medium tabular-nums">{hv != null ? fmtTelemetry(series.unit, hv) : "no observation"}</span>
        </div>
      )}
      <p className="mt-1 flex items-center gap-3 type-subtext text-muted-foreground/70">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-xs bg-muted" /> learned normal band</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-xs bg-primary/10" /> this week</span>
      </p>
    </div>
  );
}

// ── narrative model (ported from the styleguide `buildModel`) ─────────────────
// The dialog derives the same normalized read every variant shared: the move
// decomposed into slices with share-of-move, the ratio vs baseline, and the
// one-sentence headline. Kept over the wire DTO so `packages/ui` imports no
// `excellent/src` type.

type Slice = { series: TelemetrySeriesDTO; delta: number; share: number };

function sliceValues(s: TelemetrySeriesDTO): number[] {
  return s.points.filter((v): v is number => v != null);
}

/** recent / baseline (1 = on pace). Infinity when the baseline is ~0 but the
 *  recent value moved, so the narrative can say "well above" honestly. */
function ratioFor(series: TelemetrySeriesDTO): number | null {
  if (series.recent === null) return null; // no current reading = no pace
  return series.baseline > 1e-6
    ? series.recent / series.baseline
    : series.recent > 0
      ? Infinity
      : 1;
}

/** The headline sentence: "<series> ran N× its learned baseline this week —
 *  <top slice> carried M% of the move." */
function narrativeFor(series: TelemetrySeriesDTO, ratio: number | null, slices: Slice[]): string {
  // No reading this week: say that, and stop. Every clause below compares a
  // current value against the baseline, and there is no current value —
  // "held near its learned baseline" would be the same lie in prose form.
  if (ratio === null) {
    return `${series.label} recorded no activity this week, so there is no rate to compare against its learned baseline.`;
  }
  let clause: string;
  if (!Number.isFinite(ratio) || ratio >= 1.2) {
    const n = !Number.isFinite(ratio)
      ? "well above"
      : `${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)}×`;
    clause =
      n === "well above"
        ? "ran well above its learned baseline this week"
        : `ran ${n} its learned baseline this week`;
  } else if (ratio > 0 && ratio <= 0.8) {
    clause = `fell to ${Math.round(ratio * 100)}% of its learned baseline this week`;
  } else {
    clause = "held near its learned baseline this week";
  }
  const top = slices.find((s) => Math.abs(s.share) >= 0.05);
  const tail = top
    ? ` — ${top.series.label} carried ${Math.round(Math.abs(top.share) * 100)}% of the move.`
    : ".";
  return `${series.label} ${clause}${tail}`;
}

// ── the dialog ────────────────────────────────────────────────────────────────

export type InsightDialogProps = {
  sec: SectionTelemetryDTO;
  days: string[];
  recentDays: number;
  seriesId: string | null;
  onClose: () => void;
  /**
   * The footer action row (P4: watch · propose a fix → Play · open the filtered
   * view). Rendered beneath the honest-math note when present; omitted in P3, so
   * the modal shows only the honest-math line.
   */
  actions?: React.ReactNode;
};

export function InsightDialog({ sec, days, recentDays, seriesId, onClose, actions }: InsightDialogProps) {
  const all = React.useMemo(() => {
    const m = new Map<string, TelemetrySeriesDTO>();
    for (const s of sec.quiet) m.set(s.id, s);
    for (const f of sec.surfaced) m.set(f.series.id, f.series);
    return m;
  }, [sec]);

  const series = seriesId ? all.get(seriesId) : undefined;
  const finding = sec.surfaced.find((f) => f.series.id === seriesId);
  const isAnchor = sec.anchor?.id === seriesId;
  // A series with no current reading has no delta — 0 here means "no move to
  // decompose", which is what the share math below correctly does with it.
  const delta = series && series.recent !== null ? series.recent - series.baseline : 0;
  const slices = React.useMemo<Slice[]>(
    () =>
      [...all.values()]
        .filter((s) => s.parent === seriesId)
        .map((s) => {
          const d = s.recent === null ? 0 : s.recent - s.baseline;
          return { series: s, delta: d, share: Math.abs(delta) > 1e-6 ? d / delta : 0 };
        })
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)),
    [all, seriesId, delta],
  );

  const columns = React.useMemo<DataTableColumn<Slice>[]>(
    () => [
      {
        key: "label",
        header: "Slice",
        render: (row) => <span className="text-foreground">{row.series.label}</span>,
      },
      {
        key: "trend",
        header: "Trend",
        render: (row) => (
          <div className="h-6 w-24">
            <Sparkline
              values={sliceValues(row.series)}
              className="h-full w-full"
              color="var(--muted-foreground)"
              strokeOpacity={0.6}
              ariaHidden
            />
          </div>
        ),
      },
      {
        key: "move",
        header: "Baseline → this week",
        render: (row) => (
          <span className="tabular-nums text-muted-foreground">
            {fmtTelemetry(row.series.unit, row.series.baseline)} → {fmtTelemetry(row.series.unit, row.series.recent)}
          </span>
        ),
      },
      {
        key: "share",
        header: "Share of move",
        render: (row) => (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted/50">
              <div
                className="h-full rounded-full bg-primary/60"
                style={{ width: `${Math.min(100, Math.abs(row.share) * 100)}%` }}
              />
            </div>
            <span className="w-9 text-right tabular-nums text-muted-foreground">
              {Math.round(Math.abs(row.share) * 100)}%
            </span>
          </div>
        ),
      },
      {
        key: "z",
        header: "Deviation",
        render: (row) => (
          <span
            className={cn(
              "tabular-nums",
              Math.abs(row.series.z) >= 2 ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            z {row.series.z.toFixed(1)}
          </span>
        ),
      },
    ],
    [],
  );

  if (!series) return null;

  const ratio = ratioFor(series);
  const headline = narrativeFor(series, ratio, slices);
  const subhead = `${fmtTelemetry(series.unit, series.baseline)} normal → ${fmtTelemetry(series.unit, series.recent)} this week`;
  // The finding tone — a STATE cue, so it lands in the stat band (the "This week"
  // StatCard's leadingDot + label), never beside the title (PeekDialog forbids a
  // tag in the header block).
  const tone = isAnchor
    ? { intent: "info" as const, label: "Anchor" }
    : finding
      ? finding.kind === "regression"
        ? { intent: (finding.severity === "high" ? "danger" : "warning") as "danger" | "warning", label: "Needs attention" }
        : finding.kind === "improvement"
          ? { intent: "success" as const, label: "Improved" }
          : { intent: "info" as const, label: "Shifted" }
      : { intent: "neutral" as const, label: "In range" };

  return (
    <PeekDialog
      open
      onClose={onClose}
      eyebrow={sec.label}
      title={headline}
      description={
        <span className="tabular-nums">
          {subhead} · deviation z {series.z.toFixed(1)}
          {isAnchor ? " · the section's anchor" : ""}
        </span>
      }
      // The rail hosting this dialog persists across routes, so Expand must ALSO
      // close (`onExpand`) or the modal would strand over the destination page.
      expandHref={series.viewHref ?? undefined}
      expandLabel="Open the filtered view"
      onExpand={onClose}
    >
      <TrendChart series={series} days={days} recentDays={recentDays} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          title="This week"
          value={fmtTelemetry(series.unit, series.recent)}
          leadingDot={tone.intent}
          description={tone.label}
        />
        <StatCard
          title="Learned baseline"
          value={fmtTelemetry(series.unit, series.baseline)}
          description={`± ${fmtTelemetry(series.unit, series.spread)}`}
        />
        <StatCard
          title="Deviation"
          value={`z ${series.z.toFixed(1)}`}
          description={`volume weight ${series.mass.toFixed(2)}`}
        />
        <StatCard
          title="Anchor coupling"
          value={(series.coupling ?? 0).toFixed(2)}
          description={isAnchor ? "is the anchor" : "vs section velocity"}
        />
      </div>

      {slices.length > 0 && (
        <PeekDialogSection title="What's driving it">
          <DataTable columns={columns} rows={slices} rowKey={(r) => r.series.id} wrapInCard={false} />
        </PeekDialogSection>
      )}

      <p className="type-subtext text-muted-foreground/70">
        {`Baseline = 10%-trimmed mean ± robust spread over the prior ${days.length - recentDays} days, learned from this workspace's own history — no hand-set target.`}
      </p>
      {actions ? <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div> : null}
    </PeekDialog>
  );
}
